import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from "recharts";
import Handler from "../../Helpers/Handler";
import { currencyFormatter, formatDate } from "../../Helpers/Utils";
import { Skeleton, Stat } from "../../Components/Needed";
import ListLayout from "../../Components/List";
import CouponFormModal from "./CouponFormModal";
import useListParams from "../../Helpers/useListParams";

const INITIAL_FILTERS = { search: "", searchKey: "name", count: 10, status: "", audience: "" };
const SEARCH_KEYS = [{ key: "name", value: "Code / name / description" }];

// Coupon list + a small analytics dashboard up top (last-30-days by default,
// no date picker in this first pass - matches the "richer analytics" scope
// confirmed for this feature). See claude/COUPON_MANAGEMENT_PLAN.md.
const CouponList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const [summary, setSummary] = useState(null);
    const [timeseries, setTimeseries] = useState([]);
    const [top, setTop] = useState([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);

    const filterItems = {
        dropdown: [
            {
                key: "status",
                name: "Status",
                options: [
                    { key: "", name: "All" },
                    { key: "true", name: "Active" },
                    { key: "false", name: "Inactive" },
                ],
            },
            {
                key: "audience",
                name: "Audience",
                options: [
                    { key: "", name: "All" },
                    { key: "all", name: "Everyone" },
                    { key: "targeted", name: "Targeted" },
                ],
            },
        ],
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/admin/coupon/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.status !== "" ? { status: filters.status === "true" } : {}),
                ...(filters.audience ? { audience: filters.audience } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.status, filters.audience]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            setAnalyticsLoading(true);
            const [summaryRes, seriesRes, topRes] = await Promise.all([
                Handler({ method: "get", url: "/admin/coupon/analytics/summary" }),
                Handler({ method: "get", url: "/admin/coupon/analytics/timeseries" }),
                Handler({ method: "get", url: "/admin/coupon/analytics/top", params: { limit: 6 } }),
            ]);
            setSummary(summaryRes?.success ? summaryRes?.data?.data : null);
            setTimeseries(seriesRes?.success ? seriesRes?.data?.data || [] : []);
            setTop(topRes?.success ? topRes?.data?.data || [] : []);
            setAnalyticsLoading(false);
        })();
    }, []);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const openCreate = () => setShowForm(true);
    const closeForm = () => setShowForm(false);
    const onSaved = () => { setShowForm(false); load(); };

    const columns = [
        { key: "couponCode", name: "Code", mandatory: true },
        { key: "name", name: "Name", mandatory: true },
        {
            key: "discountValue",
            name: "Discount",
            render: (row) => row.discountType === "percentage"
                ? `${row.discountValue}% (up to ${currencyFormatter(row.maxDiscount || 0)})`
                : currencyFormatter(row.discountValue || 0),
        },
        {
            key: "audience",
            name: "Audience",
            render: (row) => <span className="text-capitalize">{row.audience === "all" ? "Everyone" : "Targeted"}</span>,
        },
        {
            key: "redeemedCount",
            name: "Redeemed",
            render: (row) => `${row.redeemedCount || 0}${row.usageLimit ? ` / ${row.usageLimit}` : ""}`,
        },
        { key: "expireAt", name: "Expires", render: (row) => row.expireAt ? formatDate(row.expireAt) : "No expiry" },
        {
            key: "status",
            name: "Status",
            render: (row) => <span className={`badge rounded-pill ${row.status ? "text-bg-success" : "text-bg-secondary"}`}>{row.status ? "Active" : "Inactive"}</span>,
        },
    ];

    return (
        <>
            <div className="container-fluid pt-4">
                <div className="row g-3 mb-2">
                    <Stat label="Total discount given (30d)" value={analyticsLoading ? "..." : currencyFormatter(summary?.totalDiscountGiven || 0)} />
                    <Stat label="Redemptions (30d)" value={analyticsLoading ? "..." : (summary?.totalRedemptions || 0)} />
                    <Stat label="Active coupons" value={analyticsLoading ? "..." : (summary?.activeCoupons || 0)} />
                    <Stat label="Avg. discount / redemption" value={analyticsLoading ? "..." : currencyFormatter(summary?.avgDiscount || 0)} />
                </div>

                <div className="row g-3 mb-1">
                    <div className="col-12 col-lg-7">
                        <div className="card border-0 shadow-sm rounded-4 p-3 h-100">
                            <h6 className="fw-semibold mb-3">Discount given over time (last 30 days)</h6>
                            {analyticsLoading ? <Skeleton height={220} /> : timeseries.length === 0 ? (
                                <div className="text-muted small text-center py-5">No committed redemptions in this range yet.</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={timeseries} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(value, key) => key === "discount" ? currencyFormatter(value) : value} />
                                        <Line type="monotone" dataKey="discount" name="Discount given" stroke="#0d6efd" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="card border-0 shadow-sm rounded-4 p-3 h-100">
                            <h6 className="fw-semibold mb-3">Top coupons (30d)</h6>
                            {analyticsLoading ? <Skeleton height={220} /> : top.length === 0 ? (
                                <div className="text-muted small text-center py-5">No committed redemptions in this range yet.</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={top} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="couponCode" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(value) => currencyFormatter(value)} />
                                        <Bar dataKey="discount" name="Discount given" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ListLayout
                title="Coupons"
                description="Discount codes customers can apply at checkout, or grant to specific customers."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by code, name, or description..."
                onRowClick={(row) => navigate(`/coupon/${row.couponCode}`)}
                addList={openCreate}
            />

            {showForm ? <CouponFormModal coupon={null} onClose={closeForm} onSaved={onSaved} /> : ""}
        </>
    );
};

export default CouponList;
